import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, UseFormSetError, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { DateTimePicker, LocaleEnum, getLanguageCodeByLocale } from '@systemeio/ui-frontend'
import { Emoji } from 'emoji-mart'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import useSWR, { useSWRConfig } from 'swr'
import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import { httpClient } from 'shared/api/http-client'
import { PrimaryButton } from 'shared/components/button'
import CheckBoxSwitcher from 'shared/components/checkbox-switcher'
import Modal from 'shared/components/modal'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import PlusIcon from 'shared/icons/circle-plus-icon'
import { determineTimezone } from 'shared/utils/date-time/determine-timezone'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import { generatePathFromName } from 'modules/community/hooks/path-utils'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { PostInterface } from 'modules/community/types/post-interface'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'
import PostAttachments from './components/attachments'
import AddPostContentEditor, { EditorRef } from './components/content'
import AddPostInputField from './components/input-field'
import AddPostTopicSelect from './components/topic-select'
import { CreatePostFormValues } from './types'
import { validateNotPastDate } from './validations'

const defaultValues: Omit<CreatePostFormValues, 'scheduledTo'> = {
  title: '',
  path: '',
  content: '',
}

type ErrorFields = Parameters<UseFormSetError<CreatePostFormValues>>[0]

const errorFields: ErrorFields[] = ['title', 'path', 'topic', 'content', 'root', 'scheduledTo']

interface AddPostProps {
  onAdd?: (post: PostInterface) => void
}

function AddPost({ onAdd }: AddPostProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const { user } = useUser()
  const { topicPath } = router.query

  const [opened, setOpened] = useState(false)
  const [displaySchedulingOptions, setDisplaySchedulingOptions] = useState(false)
  const { data: community, isValidating } = useCommunityWithTopics()
  const { mutate } = useSWRConfig()
  const { mutate: mutatePostPending } = useSWR<PostInterface[]>('pending-posts')
  const { isUploading } = usePostAttachmentsContext()

  const formMethods = useForm<CreatePostFormValues>({ defaultValues })
  const canSchedulePosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const editorRef = useRef<EditorRef>(null)

  function convertNameToPath(e: React.FocusEvent<HTMLInputElement>) {
    if (!e.target.value) {
      formMethods.setValue('path', generatePathFromName(formMethods.getValues('title')))
    }
  }

  const handleAddEmoji = (emoji: typeof Emoji.Props) => {
    editorRef.current?.insertEmoji(emoji.native)
  }

  const clearForm = useCallback(() => {
    formMethods.reset(defaultValues)
    editorRef.current?.clear()
  }, [])

  const clearErrors = useCallback(() => {
    formMethods.clearErrors()
    editorRef.current?.clearError()
  }, [])

  const setError = useCallback(
    (name: ErrorFields, error?: string) => formMethods.setError(name, { message: error }),
    [],
  )

  const onSubmit: SubmitHandler<CreatePostFormValues> = async (formData: CreatePostFormValues) => {
    if (!community) {
      return
    }

    clearErrors()

    const { title, path, content, topic, attachments, scheduledTo } = formData
    try {
      const { data } = await httpClient.post<PostInterface>(
        `${COMMUNITY_API_PREFIX}/${community.path}/post`,
        {
          title: DOMPurify.sanitize(title),
          path: DOMPurify.sanitize(path),
          content,
          topic,
          scheduledTo,
          attachments: escapeAttachmentLocalFields(attachments),
        },
      )
      if (scheduledTo) {
        toast.success(t('community.add_post.scheduled_message'))
      } else if (data.status === PostStatusEnum.Approved) {
        const selectedTopic = community?.topics.find(t => t.id === topic)
        const mutateKey =
          selectedTopic && selectedTopic.path === topicPath ? 'topic-posts' : 'posts'
        await mutate<PostInterface[]>(mutateKey, prev => (prev ? [data, ...prev] : [data]))
      } else {
        toast.success(t('community.add_post.sent_to_approval_message'))
        await mutatePostPending((prevPosts = []) => [...prevPosts, data], false)
      }

      onAdd?.(data)
      setOpened(false)
      clearForm()
      setDisplaySchedulingOptions(false)
    } catch (e) {
      if (e instanceof BadRequest) {
        errorFields.forEach(name => {
          const error = e as BadRequest
          if (error.errors?.fields?.[name]) {
            setError(name, error.errors.fields[name].join('\n'))
          }

          if (name === 'root' && error.errors?.common) {
            setError(name, error.errors.common.join('\n'))
          }
        })
      } else if (e instanceof InternalError) {
        setError('root', t('core.error.internal_error_message'))
      }
    }
  }

  useEffect(() => {
    if (!opened) {
      clearForm()
      setDisplaySchedulingOptions(false)
      clearErrors()
    }
  }, [opened, clearForm, clearErrors])

  if (!community || isValidating) {
    return <div className="mb-10 h-28 animate-pulse rounded-lg bg-skeleton-primary" />
  }

  const commonError = formMethods.formState.errors.root?.message
  const scheduledToError = formMethods.formState.errors.scheduledTo?.message

  return (
    <>
      <div className="mb-8 flex flex-col items-end bg-background-secondary p-5 lg:rounded-lg">
        <button
          className="h-12 w-full cursor-pointer resize-none overflow-hidden rounded-md border border-text-secondary/30 px-5 text-start text-text-secondary focus:outline-none"
          onClick={() => setOpened(true)}
        >
          {t('home.add_post.text_area.placeholder')}
        </button>
      </div>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t('settings.create_post.modal.title')}
      >
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <AddPostInputField name="title" maxLength={256} label={t('home.add_post.title')} />
            <AddPostInputField
              name="path"
              maxLength={32}
              onFocus={convertNameToPath}
              label={t('home.add_post.path')}
            />
            <AddPostTopicSelect />
            <AddPostContentEditor ref={editorRef} />
            <PostAttachments onAddEmoji={handleAddEmoji} />

            {canSchedulePosts && (
              <>
                <div className="mb-6 h-0 w-full border-t border-text-secondary/30" />

                <div className="mb-6">
                  <div className="flex items-center gap-x-3">
                    <CheckBoxSwitcher
                      onChange={setDisplaySchedulingOptions}
                      selected={displaySchedulingOptions}
                    />
                    <p className="text-base font-medium text-text-primary">
                      {t('home.add_post.scheduling_post.label')}
                    </p>
                  </div>
                  {displaySchedulingOptions && (
                    <div className="pt-2">
                      <p className="text-base text-text-primary/50">
                        {t('settings.schedule_post.modal.description')}
                      </p>
                      <Controller
                        name="scheduledTo"
                        control={formMethods.control}
                        shouldUnregister
                        defaultValue={null}
                        rules={{
                          required: {
                            value: true,
                            message: t('validation.date_time_required_error'),
                          },
                          validate: value => validateNotPastDate(t, value),
                        }}
                        render={({ field }) => (
                          <DateTimePicker
                            wrapperButtonInputClassName="pt-4"
                            labelClassName="text-base text-text-primary font-medium"
                            label={t('home.add_post.date_time_picker.label')}
                            value={field.value}
                            onChange={date =>
                              field.onChange(date ? new Date(date).toISOString() : null)
                            }
                            timezone={determineTimezone(user)}
                            createdAt={isAuthorizedUser(user) ? user.createdAt : undefined}
                            intlLocale={getLanguageCodeByLocale(
                              user?.dashboardLocale || LocaleEnum.ENGLISH,
                            )}
                            minDate={new Date()}
                          />
                        )}
                      />
                      {scheduledToError && (
                        <p className="mt-2 text-sm text-danger">{scheduledToError}</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end gap-x-2">
              <PrimaryButton type="submit">
                <PlusIcon />
                {t('home.add_post.button.title')}
              </PrimaryButton>
            </div>
            <div>{commonError && <p className="mt-2 text-sm text-danger">{commonError}</p>}</div>
          </form>
        </FormProvider>
      </Modal>
    </>
  )
}

export default AddPost
